import { useState, useEffect, useRef } from 'react';
import {
    defaultOnPageSelfScheduleConfig,
    customQuoteOnPageSelfScheduleConfig,
    SECONDARY_CTA,
    OnPageSelfScheduleConfig,
} from '../components/forms/selfSchedule/onPageSelfScheduleConfigs';
import { ON_PAGE_CUSTOM_QUOTE_ENABLED } from '../utils/selfScheduleConfig';

// Extend Window interface to allow Optimizely to set a synchronous override flag
// before React mounts, so the hook can pick it up on initialization.
declare global {
    interface Window {
        __onPageFormConfigOverride?: string;
    }
}

const CONFIG_MAP: Record<string, OnPageSelfScheduleConfig> = {
    defaultOnPageSelfScheduleConfig,
    customQuoteOnPageSelfScheduleConfig,
};

const EVENT_NAME = 'onpage_form_config_override';

// The kill-switch wins over both the placement default and an Optimizely override:
// with GATSBY_ONPAGE_CUSTOM_QUOTE_ENABLED off, a quote config degrades to the
// Ask A Question behavior. Only the CTA and its label change — the placement's
// title, subtitle and schedule label are untouched, so a future config that pairs
// its own copy with a quote CTA keeps that copy when the switch is off.
function applyKillSwitch(config: OnPageSelfScheduleConfig): OnPageSelfScheduleConfig {
    if (config.secondaryCta === SECONDARY_CTA.quote && !ON_PAGE_CUSTOM_QUOTE_ENABLED) {
        return {
            ...config,
            secondaryCta: SECONDARY_CTA.question,
            buttonLabels: {
                ...config.buttonLabels,
                inquiry: defaultOnPageSelfScheduleConfig.buttonLabels.inquiry,
            },
        };
    }
    return config;
}

/**
 * Resolves the on-page self-schedule form config for a placement (WEBDEV-4259).
 * Same mechanism as useConsultFormConfig: Optimizely (or any other caller) can swap
 * the config at runtime by dispatching a CustomEvent or setting
 * window.__onPageFormConfigOverride before React mounts.
 *
 * Example usage from Optimizely campaign JS:
 *   window.__onPageFormConfigOverride = 'customQuoteOnPageSelfScheduleConfig';
 *   window.dispatchEvent(new CustomEvent('onpage_form_config_override', { detail: 'customQuoteOnPageSelfScheduleConfig' }));
 *   // to restore the placement default:
 *   window.dispatchEvent(new CustomEvent('onpage_form_config_override', { detail: null }));
 */
export function useOnPageSelfScheduleConfig(defaultConfig: OnPageSelfScheduleConfig): OnPageSelfScheduleConfig {
    const defaultConfigRef = useRef(defaultConfig);
    defaultConfigRef.current = defaultConfig;

    const resolveConfig = (name: string | null | undefined): OnPageSelfScheduleConfig =>
        applyKillSwitch((name && CONFIG_MAP[name]) || defaultConfigRef.current);

    const [config, setConfig] = useState<OnPageSelfScheduleConfig>(() => {
        if (typeof window !== 'undefined') {
            return resolveConfig(window.__onPageFormConfigOverride);
        }
        return applyKillSwitch(defaultConfig);
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handler = (event: Event) => {
            setConfig(resolveConfig((event as CustomEvent<string | null>).detail));
        };

        window.addEventListener(EVENT_NAME, handler);
        return () => window.removeEventListener(EVENT_NAME, handler);
    }, []);

    return config;
}
